import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { cn } from '../lib/utils';
import {
  ShoppingCart,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import quotationService from '../services/quotationService';

const PartialConversionDialog = ({ open, onClose, quotation, onConversionComplete }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [conversionStatus, setConversionStatus] = useState(null);

  // Track selected products and their quantities
  const [selectedProducts, setSelectedProducts] = useState({});
  // product_index -> { selected: boolean, quantity: number }

  // Load conversion status when dialog opens
  const loadConversionStatus = useCallback(async () => {
    if (!quotation?._id) return;

    try {
      setLoading(true);
      setError('');
      const status = await quotationService.getConversionStatus(quotation._id);
      setConversionStatus(status);

      // Initialize selected products state
      const initialSelection = {};
      status.products.forEach((product) => {
        initialSelection[product.product_index] = {
          selected: false,
          quantity: product.remaining_quantity,
          maxQuantity: product.remaining_quantity
        };
      });
      setSelectedProducts(initialSelection);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load conversion status');
    } finally {
      setLoading(false);
    }
  }, [quotation]);

  useEffect(() => {
    if (open && quotation) {
      loadConversionStatus();
    }
  }, [open, quotation, loadConversionStatus]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedProducts({});
      setConversionStatus(null);
      setError('');
    }
  }, [open]);

  const handleSelectionChange = (productIndex, checked) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productIndex]: {
        ...prev[productIndex],
        selected: checked
      }
    }));
  };

  const handleQuantityChange = (productIndex, value) => {
    const numValue = parseInt(value) || 0;
    const maxQty = selectedProducts[productIndex]?.maxQuantity || 0;

    setSelectedProducts(prev => ({
      ...prev,
      [productIndex]: {
        ...prev[productIndex],
        quantity: Math.min(Math.max(0, numValue), maxQty)
      }
    }));
  };

  const getSelectedItems = () => {
    return Object.entries(selectedProducts)
      .filter(([_, item]) => item.selected && item.quantity > 0)
      .map(([index, item]) => ({
        product_index: parseInt(index),
        quantity: item.quantity
      }));
  };

  const selectedCount = Object.values(selectedProducts).filter(p => p.selected && p.quantity > 0).length;
  const totalSelectedQty = Object.values(selectedProducts)
    .filter(p => p.selected && p.quantity > 0)
    .reduce((sum, p) => sum + p.quantity, 0);

  const hasAvailableProducts = conversionStatus?.products?.some(p => p.remaining_quantity > 0) ?? false;

  const handleSubmit = async () => {
    const selectedItems = getSelectedItems();

    if (selectedItems.length === 0) {
      setError('Please select at least one product to convert');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const response = await quotationService.convertQuotationToOrderPartial(
        quotation._id,
        selectedItems
      );

      onConversionComplete?.(response);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Conversion failed');
    } finally {
      setSubmitting(false);
    }
  };

  const renderProductRow = (product, index) => {
    const isFullyConverted = product.remaining_quantity === 0;
    const selection = selectedProducts[product.product_index] || {};

    return (
      <tr
        key={index}
        className={cn(
          "border-b",
          isFullyConverted && "bg-muted/50 text-muted-foreground"
        )}
      >
        <td className="p-3">
          <Checkbox
            checked={selection.selected || false}
            onCheckedChange={(checked) => handleSelectionChange(product.product_index, checked)}
            disabled={isFullyConverted}
          />
        </td>
        <td className="p-3">
          <div className="font-medium">{product.product_name || '-'}</div>
          <div className="text-sm text-muted-foreground">{product.product_code}</div>
          {product.specification && (
            <div className="text-xs text-muted-foreground mt-1">{product.specification}</div>
          )}
        </td>
        <td className="p-3 text-center">{product.original_quantity}</td>
        <td className="p-3 text-center">
          {product.converted_quantity > 0 ? (
            <span className="text-green-600">{product.converted_quantity}</span>
          ) : (
            <span className="text-muted-foreground">0</span>
          )}
        </td>
        <td className="p-3 text-center">
          {isFullyConverted ? (
            <span className="flex items-center justify-center gap-1 text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              0
            </span>
          ) : (
            <span className="text-amber-600">{product.remaining_quantity}</span>
          )}
        </td>
        <td className="p-3">
          {!isFullyConverted && (
            <Input
              type="number"
              min={0}
              max={product.remaining_quantity}
              value={selection.quantity || ''}
              onChange={(e) => handleQuantityChange(product.product_index, e.target.value)}
              disabled={!selection.selected}
              className={cn(
                "w-24",
                !selection.selected && "bg-muted"
              )}
            />
          )}
        </td>
      </tr>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            部分转换为合同 (Partial Convert to Sales Order)
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : !hasAvailableProducts ? (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                All products in this quotation have been fully converted to sales orders.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Info about quotation */}
              <Card className="mb-4">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">报价单号: </span>
                      <span className="font-medium">{quotation?.quotation_id || '-'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">客户: </span>
                      <span className="font-medium">{quotation?.client_name || '-'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Product selection table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="p-3 text-left w-12">选择</th>
                      <th className="p-3 text-left">产品</th>
                      <th className="p-3 text-center w-24">原数量</th>
                      <th className="p-3 text-center w-24">已转换</th>
                      <th className="p-3 text-center w-24">剩余</th>
                      <th className="p-3 text-left w-28">转换数量</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversionStatus?.products?.map((product, index) =>
                      renderProductRow(product, index)
                    )}
                  </tbody>
                </table>
              </div>

              {/* Conversion history */}
              {conversionStatus?.conversion_history?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2">转换历史 (Conversion History)</h4>
                  <div className="space-y-2">
                    {conversionStatus.conversion_history.map((entry, index) => (
                      <Card key={index} className="bg-muted/50">
                        <CardContent className="py-2 px-3">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">{entry.sales_order_id}</span>
                            <span className="text-muted-foreground">
                              {new Date(entry.converted_at).toLocaleString()}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {entry.items?.length} product(s) converted
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-muted-foreground">
              {selectedCount > 0 && (
                <span>
                  已选择 {selectedCount} 个产品, 共 {totalSelectedQty} 件
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                取消
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || selectedCount === 0 || loading}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    转换中...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    转换为合同
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PartialConversionDialog;
